import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import ImageUpload from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, Copy, Check, Store as StoreIcon, SlidersHorizontal, LogIn, KeyRound, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const emptyForm = {
  name: "", owner_name: "", owner_email: "", mobile: "",
  logo_url: "", address: "", pincode: "", city: "", state: "",
  gst: "", delivery_radius_km: 5, status: "active",
};

// Sprint — Per-store feature toggles surfaced by the Super Admin.
// Order = the order Store Admins see items in their sidebar.
// `default` decides the initial UI state for stores that never had features
// configured (matches the "unset → enabled" behaviour on the backend).
const FEATURE_TOGGLES = [
  { key: "pos",             label: "POS + Invoice History", desc: "Full-screen point-of-sale + past invoices" },
  { key: "orders",          label: "Orders",                desc: "Order queue, status updates, delivery" },
  { key: "products",        label: "Products",              desc: "Product catalog CRUD" },
  { key: "bulk_upload",     label: "Bulk Product Upload",   desc: ".xlsx / .csv import" },
  { key: "categories",      label: "Categories",            desc: "Category tree management" },
  { key: "brands",          label: "Brands",                desc: "Brand directory" },
  { key: "inventory",       label: "Inventory",             desc: "Stock adjustments, low-stock alerts" },
  { key: "suppliers",       label: "Suppliers",             desc: "Supplier ledger" },
  { key: "purchases",       label: "Purchase Orders",       desc: "PO creation, GRN" },
  { key: "warehouses",      label: "Warehouses",            desc: "Multi-warehouse setup" },
  { key: "customers",       label: "Customers",             desc: "Customer directory" },
  { key: "employees",       label: "Employees",             desc: "Staff & role management" },
  { key: "coupons",         label: "Coupons & Marketing",   desc: "Promotions, coupons, banners" },
  { key: "deal_baazar",     label: "Deal Baazar",           desc: "Time-boxed deal campaigns" },
  { key: "homepage_layout", label: "Homepage Layout",       desc: "Section ordering / hero" },
  { key: "store_status",    label: "Store Status",          desc: "Open / close store toggle" },
  { key: "delivery_zones",  label: "Delivery Zones",        desc: "Pincode & radius rules" },
  { key: "ui_customization",label: "Store UI Customization",desc: "Colors, fonts, category cards" },
  { key: "store_settings",  label: "Store Settings",        desc: "Profile, GST, delivery fees" },
  { key: "reports",         label: "Reports",               desc: "Sales, best sellers, tax reports" },
];
const DEFAULT_FEATURES = Object.fromEntries(FEATURE_TOGGLES.map((f) => [f.key, true]));

export default function Stores() {
  const { user, impersonate } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [credentials, setCredentials] = useState(null); // { code, name, owner_email, temp_password, login_url }
  const [copied, setCopied] = useState(false);
  // Sprint — Feature toggle dialog state.
  const [featTarget, setFeatTarget] = useState(null);   // the store row being edited
  const [featMap, setFeatMap] = useState({});           // {feature_key: bool}
  const [featSaving, setFeatSaving] = useState(false);
  // Sprint — Reset-owner-password state.
  const [pwResult, setPwResult] = useState(null);       // {temp_password, owner_email, code, name}
  const [pwCopied, setPwCopied] = useState(false);
  const [impLoading, setImpLoading] = useState(null);   // store id currently being impersonated

  // POS Permissions — store-wise Advanced POS Permissions dialog.
  // Reuses `settings.commerce.pos_controls` (already free-form on the
  // backend, no new API) so future permissions can be added by dropping
  // another Switch here.
  const [posSettingsTarget, setPosSettingsTarget] = useState(null); // store row being edited
  const [posSettingsMap, setPosSettingsMap] = useState({
    allow_inline_mrp_edit: false,
    allow_inline_selling_price_edit: false,
  });
  const [posSettingsLoading, setPosSettingsLoading] = useState(false);
  const [posSettingsSaving, setPosSettingsSaving] = useState(false);

  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const fetchRows = async () => {
    setLoading(true);
    try { const { data } = await api.get("/stores?limit=100"); setRows(data.items || []); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (isSuperAdmin) fetchRows(); }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return (
      <div className="p-6 md:p-8">
        <PageHeader title="Stores" subtitle="Restricted" />
        <div className="card-soft p-6 text-sm text-muted-foreground">
          You don't have access to this module. Contact a Super Admin.
        </div>
      </div>
    );
  }

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      ...emptyForm,
      name: r.name || "",
      owner_name: r.owner_name || "",
      owner_email: r.owner_email || "",
      mobile: r.mobile || "",
      logo_url: r.logo_url || "",
      address: r.address?.line1 || "",
      pincode: r.address?.pincode || "",
      city: r.address?.city || "",
      state: r.address?.state || "",
      gst: r.gst || "",
      delivery_radius_km: r.delivery?.radius_km ?? 5,
      status: r.status || "active",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.owner_name.trim() || !form.owner_email.trim() || !form.mobile.trim()) {
      toast.error("Store name, owner name, email and mobile are required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        delete payload.owner_email; // email is fixed after creation
        await api.patch(`/stores/${editing.id}`, payload);
        toast.success("Store updated");
      } else {
        const { data } = await api.post("/stores", form);
        setCredentials({
          code: data.code,
          name: data.name,
          owner_email: data.owner_email,
          temp_password: data.temp_password,
          login_url: data.login_url || "/login",
        });
        toast.success(`Store ${data.code} created`);
      }
      setOpen(false);
      fetchRows();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (r) => {
    try {
      const action = r.status === "active" ? "disable" : "enable";
      await api.post(`/stores/${r.id}/${action}`);
      toast.success(`Store ${action}d`);
      fetchRows();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/stores/${deleteTarget.id}`);
      toast.success("Store deleted");
      setDeleteTarget(null);
      fetchRows();
    } catch (e) { toast.error(errMsg(e)); }
  };

  // Sprint — Feature toggle handlers.
  const openFeatures = (r) => {
    const existing = r.features || {};
    // Merge existing selections onto defaults so newly-added toggles show ON.
    setFeatMap({ ...DEFAULT_FEATURES, ...existing });
    setFeatTarget(r);
  };
  const toggleFeat = (key, v) => setFeatMap((m) => ({ ...m, [key]: v }));
  const bulkFeat = (v) => setFeatMap(Object.fromEntries(FEATURE_TOGGLES.map((f) => [f.key, v])));
  const saveFeatures = async () => {
    if (!featTarget) return;
    setFeatSaving(true);
    try {
      await api.patch(`/stores/${featTarget.id}/features`, { features: featMap });
      toast.success(`Features updated for ${featTarget.code}`);
      setFeatTarget(null);
      fetchRows();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setFeatSaving(false); }
  };

  // POS Permissions handlers — load / save under settings.commerce.pos_controls.
  // Uses the X-Store-Id header override so a SUPER_ADMIN can address any
  // store without switching context. Zero backend rewrite.
  const openPosSettings = async (r) => {
    setPosSettingsTarget(r);
    setPosSettingsMap({
      allow_inline_mrp_edit: false,
      allow_inline_selling_price_edit: false,
    });
    setPosSettingsLoading(true);
    try {
      const { data } = await api.get("/store/settings", { headers: { "X-Store-Id": r.id } });
      const pc = data?.settings?.commerce?.pos_controls || {};
      setPosSettingsMap({
        allow_inline_mrp_edit: !!pc.allow_inline_mrp_edit,
        allow_inline_selling_price_edit: !!pc.allow_inline_selling_price_edit,
      });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setPosSettingsLoading(false); }
  };
  const savePosSettings = async () => {
    if (!posSettingsTarget) return;
    setPosSettingsSaving(true);
    try {
      const headers = { "X-Store-Id": posSettingsTarget.id };
      // Fetch current commerce subtree so PATCH doesn't wipe sibling keys
      // (store_status, pos_controls other fields, homepage etc.).
      const { data: cur } = await api.get("/store/settings", { headers });
      const commerce = { ...(cur?.settings?.commerce || {}) };
      commerce.pos_controls = { ...(commerce.pos_controls || {}), ...posSettingsMap };
      await api.patch("/store/settings/commerce", commerce, { headers });
      toast.success(`POS permissions saved for ${posSettingsTarget.code}`);
      setPosSettingsTarget(null);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setPosSettingsSaving(false); }
  };

  // Sprint — Store Admin management handlers.
  const doResetPassword = async (r) => {
    try {
      const { data } = await api.post(`/stores/${r.id}/reset-owner-password`);
      setPwResult({ ...data, login_url: data.login_url || "/login" });
      toast.success("New password generated");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const doImpersonate = async (r) => {
    setImpLoading(r.id);
    try {
      await impersonate(r.id);
      toast.success(`Now viewing ${r.code} as Store Admin`);
      navigate("/admin/dashboard");
      // Force a full reload so every context re-boots against the new session.
      setTimeout(() => window.location.assign("/admin/dashboard"), 100);
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setImpLoading(null); }
  };
  const copyPwCreds = async () => {
    if (!pwResult) return;
    const text = `Store ID: ${pwResult.code}
Store Name: ${pwResult.name}
Login URL: ${window.location.origin}${pwResult.login_url}
Email: ${pwResult.owner_email}
New Password: ${pwResult.temp_password}`;
    try {
      await navigator.clipboard.writeText(text);
      setPwCopied(true);
      setTimeout(() => setPwCopied(false), 2000);
      toast.success("Credentials copied");
    } catch { toast.error("Copy failed — select and copy manually"); }
  };

  const copyCreds = async () => {
    if (!credentials) return;
    const text = `Store ID: ${credentials.code}
Store Name: ${credentials.name}
Login URL: ${window.location.origin}${credentials.login_url}
Email: ${credentials.owner_email}
Temporary Password: ${credentials.temp_password}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Credentials copied");
    } catch { toast.error("Copy failed — select and copy manually"); }
  };

  const activeCount = rows.filter((r) => r.status === "active").length;
  const remaining = Math.max(0, 100 - activeCount);

  const columns = [
    {
      key: "code",
      header: "Store ID",
      render: (r) => (
        <span className="font-mono text-xs font-semibold text-primary" data-testid={`store-code-${r.code}`}>{r.code}</span>
      ),
    },
    {
      key: "name",
      header: "Store",
      render: (r) => (
        <div className="min-w-0">
          <div className="font-medium truncate">{r.name}</div>
          <div className="text-xs text-muted-foreground truncate">
            {r.address?.city || "—"}{r.address?.state ? `, ${r.address.state}` : ""}
          </div>
        </div>
      ),
    },
    {
      key: "owner",
      header: "Owner",
      render: (r) => (
        <div className="min-w-0">
          <div className="text-sm truncate">{r.owner_name || "—"}</div>
          <div className="text-xs text-muted-foreground truncate">{r.owner_email || "—"}</div>
        </div>
      ),
    },
    { key: "mobile", header: "Mobile", render: (r) => <span className="font-mono text-xs">{r.mobile || "—"}</span> },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <Badge variant={r.status === "active" ? "default" : "secondary"} data-testid={`store-status-${r.code}`}>
          {r.status}
        </Badge>
      ),
    },
    { key: "created_at", header: "Created", render: (r) => <span className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</span> },
    {
      key: "actions",
      header: "",
      className: "text-right",
      render: (r) => (
        <div className="flex items-center justify-end gap-1 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={() => doImpersonate(r)}
            disabled={impLoading === r.id || r.status !== "active"}
            className="h-8"
            data-testid={`impersonate-store-${r.code}`}
            title="Login as Store Admin"
          >
            <LogIn className="w-3.5 h-3.5 mr-1" />
            <span className="text-xs">{impLoading === r.id ? "…" : "Open Store"}</span>
          </Button>
          <Button size="icon" variant="ghost" onClick={() => doResetPassword(r)}
                  title="Reset owner password"
                  data-testid={`reset-pw-store-${r.code}`}>
            <KeyRound className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => toggleStatus(r)}
                  data-testid={`toggle-store-${r.code}`}>
            <Switch checked={r.status === "active"} onCheckedChange={() => toggleStatus(r)} />
          </Button>
          <Button size="icon" variant="ghost" onClick={() => openPosSettings(r)}
                  title="POS Settings — Advanced Permissions"
                  data-testid={`pos-settings-store-${r.code}`}>
            <Settings2 className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="ghost" onClick={() => openFeatures(r)}
                  title="Feature toggles"
                  data-testid={`features-store-${r.code}`}>
            <SlidersHorizontal className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-store-${r.code}`}>
            <Pencil className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="ghost" onClick={() => setDeleteTarget(r)}
                  className="text-muted-foreground hover:text-destructive" data-testid={`delete-store-${r.code}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 md:p-8" data-testid="stores-page">
      <PageHeader
        title="Stores"
        subtitle={`${activeCount} active · ${remaining} slots left (max 100)`}
        actions={
          <Button onClick={openCreate} data-testid="add-store-btn" disabled={remaining === 0}>
            <Plus className="w-4 h-4 mr-1" /> Add Store
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        testId="stores-table"
        empty={
          <div className="flex flex-col items-center gap-2">
            <StoreIcon className="w-8 h-8 opacity-60" />
            <div>No stores yet. Create the first one.</div>
          </div>
        }
      />

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.code}` : "Add Store"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Update store profile & delivery info."
                : "Provisions the store, creates a STORE_OWNER account, generates a temporary password and sets up the default warehouse."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            <div className="md:col-span-2">
              <Label>Store Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="store-name-input" />
            </div>

            <div>
              <Label>Owner Name *</Label>
              <Input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                     data-testid="store-owner-name" />
            </div>
            <div>
              <Label>Mobile *</Label>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                     data-testid="store-mobile" />
            </div>

            <div className="md:col-span-2">
              <Label>Email {editing ? "" : "*"}</Label>
              <Input type="email" value={form.owner_email} disabled={!!editing}
                     onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                     data-testid="store-owner-email" />
              {!editing && <div className="text-xs text-muted-foreground mt-1">
                A temporary password will be generated. Store owner will use this email to log in at <span className="font-mono">/login</span>.
              </div>}
            </div>

            <div className="md:col-span-2">
              {/* Sprint — Replace the plain URL input with a real image
                  uploader (gallery / camera / drag-drop). Data-URL stored
                  in the same `logo_url` field so no backend/schema change. */}
              <div className="w-full max-w-[220px]">
                <ImageUpload
                  value={form.logo_url}
                  onChange={(v) => setForm({ ...form, logo_url: v })}
                  label="Store Logo"
                  hint="Square 1:1 works best. PNG/JPG/WebP/SVG · max 3 MB."
                  aspect="square"
                  testId="store-logo-upload"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                     data-testid="store-address" />
            </div>
            <div>
              <Label>Pincode</Label>
              <Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })}
                     data-testid="store-pincode" />
            </div>
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                     data-testid="store-city" />
            </div>
            <div>
              <Label>State</Label>
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                     data-testid="store-state" />
            </div>
            <div>
              <Label>GST (optional)</Label>
              <Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })}
                     data-testid="store-gst" />
            </div>

            <div>
              <Label>Delivery Radius (km)</Label>
              <Input type="number" min="1" max="50" step="0.5"
                     value={form.delivery_radius_km}
                     onChange={(e) => setForm({ ...form, delivery_radius_km: Number(e.target.value) })}
                     data-testid="store-radius" />
            </div>
            <div className="flex items-center gap-2 mt-6">
              <Switch checked={form.status === "active"}
                      onCheckedChange={(v) => setForm({ ...form, status: v ? "active" : "disabled" })}
                      data-testid="store-status-switch" />
              <Label>Active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-store-btn">
              {saving ? "Saving…" : editing ? "Update Store" : "Create Store"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials modal — shown once after successful create */}
      <Dialog open={!!credentials} onOpenChange={(o) => !o && setCredentials(null)}>
        <DialogContent className="max-w-md" data-testid="credentials-modal">
          <DialogHeader>
            <DialogTitle>Store Created Successfully</DialogTitle>
            <DialogDescription>
              Share these credentials with the store owner. This password is shown only once.
            </DialogDescription>
          </DialogHeader>
          {credentials && (
            <div className="space-y-3 py-2 font-mono text-sm">
              <Row k="Store ID" v={credentials.code} />
              <Row k="Login URL" v={`${window.location.origin}${credentials.login_url}`} />
              <Row k="Email" v={credentials.owner_email} />
              <Row k="Temporary Password" v={credentials.temp_password} highlight />
            </div>
          )}
          <DialogFooter>
            <Button onClick={copyCreds} className="w-full" data-testid="copy-credentials-btn">
              {copied ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy Credentials</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sprint — Password reset result modal. Shows the new temp password
          once with a copy affordance. Never returned again by the backend. */}
      <Dialog open={!!pwResult} onOpenChange={(o) => !o && setPwResult(null)}>
        <DialogContent className="max-w-md" data-testid="reset-pw-modal">
          <DialogHeader>
            <DialogTitle>New Store Admin Password</DialogTitle>
            <DialogDescription>
              Share this with the store owner. The old password no longer works.
              This new password is shown only once.
            </DialogDescription>
          </DialogHeader>
          {pwResult && (
            <div className="space-y-3 py-2 font-mono text-sm">
              <Row k="Store ID" v={pwResult.code} />
              <Row k="Login URL" v={`${window.location.origin}${pwResult.login_url}`} />
              <Row k="Email" v={pwResult.owner_email} />
              <Row k="New Password" v={pwResult.temp_password} highlight />
            </div>
          )}
          <DialogFooter>
            <Button onClick={copyPwCreds} className="w-full" data-testid="copy-reset-pw-btn">
              {pwCopied ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy Credentials</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* POS Settings — Advanced POS Permissions (store-wise). */}
      <Dialog open={!!posSettingsTarget} onOpenChange={(o) => !o && setPosSettingsTarget(null)}>
        <DialogContent className="max-w-md" data-testid="pos-settings-modal">
          <DialogHeader>
            <DialogTitle>POS Settings · {posSettingsTarget?.code}</DialogTitle>
            <DialogDescription>
              Advanced POS Permissions for this store. Cashiers on the
              Native Store Admin app will see the pencil icon on the
              chosen field(s) inside the POS cart. Stock is always
              display-only in POS.
            </DialogDescription>
          </DialogHeader>
          {posSettingsLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : (
            <div className="divide-y">
              <div className="flex items-start justify-between gap-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">Allow Inline MRP Edit</div>
                  <div className="text-xs text-muted-foreground">
                    Cashier can tap the pencil next to MRP on any cart line to overwrite it.
                  </div>
                </div>
                <Switch
                  checked={posSettingsMap.allow_inline_mrp_edit}
                  onCheckedChange={(v) => setPosSettingsMap((m) => ({ ...m, allow_inline_mrp_edit: v }))}
                  data-testid="pos-perm-allow-inline-mrp-edit"
                />
              </div>
              <div className="flex items-start justify-between gap-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">Allow Inline Selling Price Edit</div>
                  <div className="text-xs text-muted-foreground">
                    Cashier can tap the pencil next to Selling Price on any cart line to overwrite it.
                  </div>
                </div>
                <Switch
                  checked={posSettingsMap.allow_inline_selling_price_edit}
                  onCheckedChange={(v) => setPosSettingsMap((m) => ({ ...m, allow_inline_selling_price_edit: v }))}
                  data-testid="pos-perm-allow-inline-sp-edit"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPosSettingsTarget(null)}>Cancel</Button>
            <Button onClick={savePosSettings} disabled={posSettingsSaving || posSettingsLoading} data-testid="save-pos-settings-btn">
              {posSettingsSaving ? "Saving…" : "Save Permissions"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sprint — Feature toggle dialog. Super Admin picks which sidebar
          modules a store's admins can see. `pos` also gates POS Invoice
          History. Unset keys default to ENABLED so old stores keep working. */}
      <Dialog open={!!featTarget} onOpenChange={(o) => !o && setFeatTarget(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="features-modal">
          <DialogHeader>
            <DialogTitle>Features · {featTarget?.code}</DialogTitle>
            <DialogDescription>
              Choose which modules the store admin can access. Disabled items
              disappear from their sidebar entirely — no error screens.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between pb-2 border-b">
            <div className="text-xs text-muted-foreground">
              {Object.values(featMap).filter(Boolean).length} of {FEATURE_TOGGLES.length} enabled
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => bulkFeat(true)}  data-testid="features-enable-all">Enable all</Button>
              <Button size="sm" variant="ghost" onClick={() => bulkFeat(false)} data-testid="features-disable-all">Disable all</Button>
            </div>
          </div>
          <div className="divide-y">
            {FEATURE_TOGGLES.map((f) => (
              <div key={f.key} className="flex items-start justify-between gap-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{f.label}</div>
                  <div className="text-xs text-muted-foreground">{f.desc}</div>
                </div>
                <Switch
                  checked={featMap[f.key] !== false}
                  onCheckedChange={(v) => toggleFeat(f.key, v)}
                  data-testid={`feature-toggle-${f.key}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFeatTarget(null)}>Cancel</Button>
            <Button onClick={saveFeatures} disabled={featSaving} data-testid="save-features-btn">
              {featSaving ? "Saving…" : "Save Features"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.code}?</AlertDialogTitle>
            <AlertDialogDescription>
              Deletion is only allowed when the store has no products and no orders.
              The Store ID <span className="font-mono">{deleteTarget?.code}</span> will never be reused.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive hover:bg-destructive/90"
                               data-testid="confirm-delete-store">
              Delete Store
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Row({ k, v, highlight }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b py-2 last:border-0">
      <span className="text-xs text-muted-foreground uppercase tracking-wider">{k}</span>
      <span className={`truncate ${highlight ? "text-primary font-semibold" : ""}`}>{v}</span>
    </div>
  );
}
